"use client";

import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../../hooks/useAuth";
import { channelsAPI, handleApiError } from "../../../services/api";
import "./TransferOwnershipModal.css";

const TransferOwnershipModal = ({ isOpen, onClose, channelId, channelName, onTransferComplete }) => {
  const { t } = useTranslation();
  const { getUserType } = useAuth();
  
  const currentUserType = getUserType();
  const getUserTypeColor = (userType) => {
    const userTypeConfig = {
      'PET_GUARDIAN': '#8548A8',
      'VETERINARIAN': '#00D4B3',
      'SERVICE_PROVIDER': '#D65745',
      'SERVICE_PROVIDER': '#D65745',
      'CLINIC_MODERATOR': '#2A8AD2',
    };
    return userTypeConfig[userType] || '#8548A8';
  };
  const userTypeColor = getUserTypeColor(currentUserType);
  
  const [members, setMembers] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedMemberId, setSelectedMemberId] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const loadMembers = async () => {
      // Only load members if the modal is open AND there is a search query
      if (isOpen && searchQuery.trim().length > 0) {
        setIsLoading(true);
        try {
          const response = await channelsAPI.getChannelMembers(channelId, {
            page: 1,
            per_page: 100,
            query: searchQuery.trim(), // Assuming the backend supports query parameter for members
          });

          if (response.data && response.data.data) {
            const transformedMembers = response.data.data
              .filter((member) => member.role !== "owner")
              .map((member) => {
                const user = member.user || {};
                const name =
                  user.name ||
                  (user.first_name && user.last_name
                    ? `${user.first_name} ${user.last_name}`
                    : user.username || "Unknown");
                return {
                  id: user.user_id || user.id || member.id,
                  name: name,
                  profileImage: user.profile_image_url || "/icons/person.png",
                  role: member.role || "member",
                };
              });
            setMembers(transformedMembers);
          }
        } catch (error) {
          handleApiError(error);
        } finally {
          setIsLoading(false);
        }
      } else {
        // Clear members if search query is cleared
        setMembers([]);
        setIsLoading(false);
      }
    };

    // Debounce search slightly to avoid too many API calls
    const timer = setTimeout(() => {
      loadMembers();
    }, 300);

    return () => clearTimeout(timer);
  }, [isOpen, channelId, searchQuery]);

  const handleTransfer = async () => {
    if (!selectedMemberId) {
      alert(
        t(
          "transferOwnership.selectMemberError",
          "Please select a member to transfer ownership to."
        )
      );
      return;
    }

    setIsSubmitting(true);
    try {
      await onTransferComplete(selectedMemberId);
      onClose();
    } catch (error) {
      handleApiError(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="transfer-ownership-modal-overlay" onClick={onClose}>
      <div className="transfer-ownership-modal" onClick={(e) => e.stopPropagation()} style={{ '--user-type-color': userTypeColor }}>
        <div className="transfer-ownership-modal-header">
          <h2 className="transfer-ownership-modal-title">
            {t(
              "transferOwnership.title",
              "Transfer Channel Ownership"
            )}
          </h2>
          <button className="transfer-ownership-modal-close" onClick={onClose}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>

        <div className="transfer-ownership-modal-content">
          <div className="transfer-ownership-modal-warning">
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
              <line x1="12" y1="8" x2="12" y2="12"></line>
              <circle cx="12" cy="16" r="1"></circle>
            </svg>
            <p>
              <strong>
                {t(
                  "transferOwnership.warningTitle",
                  'Are you sure you want to leave "{{name}}"?',
                  { name: channelName }
                )}
              </strong>
            </p>
            <p>
              {t(
                "transferOwnership.warningBody",
                "You must transfer ownership to another member before leaving. Once transferred, you will become a regular member or can leave the channel."
              )}
            </p>
          </div>

          <div className="transfer-ownership-search">
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <circle cx="11" cy="11" r="8"></circle>
              <path d="m21 21-4.35-4.35"></path>
            </svg>
            <input
              type="text"
              placeholder={t(
                "transferOwnership.searchPlaceholder",
                "Search members..."
              )}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="transfer-ownership-search-input"
            />
          </div>

          {isLoading ? (
            <div className="transfer-ownership-modal-loading">
              <div className="loading-spinner"></div>
              <p>{t("transferOwnership.loading", "Loading members...")}</p>
            </div>
          ) : (
            <>
              <label className="transfer-ownership-modal-label">
                {t(
                  "transferOwnership.selectLabel",
                  "Select a member to transfer ownership to:"
                )}
              </label>

              <div className="transfer-ownership-members-list">
                {searchQuery.trim().length === 0 ? (
                  <div className="transfer-ownership-modal-prompt">
                    <p>
                      {t(
                        "transferOwnership.searchPrompt",
                        "Start typing to search for members..."
                      )}
                    </p>
                  </div>
                ) : (() => {
                  const filtered = members.filter((member) =>
                    member.name
                      .toLowerCase()
                      .includes(searchQuery.toLowerCase())
                  );

                  if (filtered.length > 0) {
                    return filtered.map((member) => (
                      <div
                        key={member.id}
                        className={`transfer-ownership-member-item ${
                          selectedMemberId === member.id ? "selected" : ""
                        }`}
                        onClick={() => setSelectedMemberId(member.id)}
                      >
                        <img
                          src={member.profileImage || "/icons/person.png"}
                          alt={member.name}
                          className="transfer-ownership-member-avatar"
                          onError={(e) => {
                            e.target.src = "/icons/person.png";
                          }}
                        />
                        <div className="transfer-ownership-member-info">
                          <span className="transfer-ownership-member-name">
                            {member.name}
                          </span>
                          <span className="transfer-ownership-member-role">
                            {member.role}
                          </span>
                        </div>
                        {selectedMemberId === member.id && (
                          <div className="transfer-ownership-member-check">
                            <svg
                              width="20"
                              height="20"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                            >
                              <polyline points="20 6 9 17 4 12"></polyline>
                            </svg>
                          </div>
                        )}
                      </div>
                    ));
                  }

                  return (
                    <div className="transfer-ownership-modal-empty">
                      <p>
                        {t(
                          "transferOwnership.noResults",
                          "No members found matching your search."
                        )}
                      </p>
                    </div>
                  );
                })()}
              </div>
            </>
          )}
        </div>

        <div className="transfer-ownership-modal-actions">
          <button className="transfer-ownership-modal-cancel" onClick={onClose}>
            {t("transferOwnership.cancel", "Cancel")}
          </button>
          <button
            className="transfer-ownership-modal-submit"
            onClick={handleTransfer}
            disabled={isSubmitting || !selectedMemberId || members.length === 0}
          >
            {isSubmitting
              ? t("transferOwnership.submitting", "Transferring...")
              : t(
                  "transferOwnership.submit",
                  "Transfer Ownership & Leave"
                )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TransferOwnershipModal;

